import { useLazyQuery } from '@apollo/client';
import { financeListApi } from '../api';
import { APIRequestPagination, APIRequestSort, Nullable } from 'global';
import { FinanceFilters, formatFilterForFinancePayload } from 'modules/finance/filters';
import { useEffect, useMemo } from 'react';
import { FinanceTableEntity } from '../types';
import { VendorOpportunityRevenue, VendorOpportunitiesRevenueResponse } from '@trnsact/trnsact-shared-types';

function formatFinanceLogToTableData(revenues: VendorOpportunityRevenue[]): FinanceTableEntity[] {
  return revenues.map(revenue => ({
    id: revenue.vendorOpportunityId,
    opportunityId: revenue.vendorOpportunityId,
    customerName: revenue.customerName ?? null,
    locationBranch: revenue.locationBranch ?? null,
    salesRep: revenue.salesRep ?? null,
    financeMgr: revenue.financeMgr ?? null,
    dealDate: revenue.dealDate ?? null,
    estimatedCloseDate: revenue.estimatedCloseDate ?? null,
    createdDate: revenue.createdDate ?? null,
    dealAmount: revenue.dealAmount ?? null,
    equipmentSalePrice: revenue.equipmentSalePrice ?? null,
    equipmentCost: revenue.equipmentCost ?? null,
    equipmentGross: revenue.equipmentGross ?? null,
    equipmentMarginPercent: revenue.equipmentMarginPercent ?? null,
    financeUsed: revenue.financeUsed ?? null,
    financeGross: revenue.financeGross ?? null,
    financeProfit: revenue.financeProfit ?? null,
    financeMarginPercent: revenue.financeMarginPercent ?? null,
    internalFinanceTier: revenue.internalFinanceTier ?? null,
    lender: revenue.lender ?? null,
    docFeeAmount: revenue.docFeeAmount ?? null,
    escSold: revenue.escSold ?? null,
    escVendor: revenue.escVendor ?? null,
    escGross: revenue.escGross ?? null,
    escCost: revenue.escCost ?? null,
    escProfit: revenue.escProfit ?? null,
    gapSold: revenue.gapSold ?? null,
    gapVendor: revenue.gapVendor ?? null,
    gapGross: revenue.gapGross ?? null,
    gapCost: revenue.gapCost ?? null,
    gapProfit: revenue.gapProfit ?? null,
    insuranceSold: revenue.insuranceSold ?? null,
    insuranceVendor: revenue.insuranceVendor ?? null,
    insuranceGross: revenue.insuranceGross ?? null,
    insuranceCost: revenue.insuranceCost ?? null,
    insuranceProfit: revenue.insuranceProfit ?? null,
    roadsideSold: revenue.roadsideSold ?? null,
    roadsideVendor: revenue.roadsideVendor ?? null,
    roadsideGross: revenue.roadsideGross ?? null,
    roadsideCost: revenue.roadsideCost ?? null,
    roadsideProfit: revenue.roadsideProfit ?? null,
  }));
}

export function useFinanceData(
  currentFilterConfig: Nullable<Partial<FinanceFilters>>,
  pagination: APIRequestPagination,
  sorting?: APIRequestSort
) {
  const [getRevenueData, { data: revenueData, loading: isRevenueLoading }] = useLazyQuery<{
    vendorOpportunitiesRevenue: VendorOpportunitiesRevenueResponse;
  }>(financeListApi.vendorOpportunitiesRevenue, {
    notifyOnNetworkStatusChange: true,
  });

  const input = useMemo(() => {
    const filters = formatFilterForFinancePayload(currentFilterConfig);
    return {
      offset: (pagination.page - 1) * pagination.perPage,
      limit: pagination.perPage,
      sorting,
      filters,
    };
  }, [pagination, sorting, currentFilterConfig]);

  useEffect(() => {
    getRevenueData({ variables: { input } });
  }, [input, getRevenueData]);

  return {
    isLoading: isRevenueLoading,
    financeList: revenueData?.vendorOpportunitiesRevenue.revenues
      ? formatFinanceLogToTableData(revenueData.vendorOpportunitiesRevenue.revenues)
      : [],
    total: revenueData?.vendorOpportunitiesRevenue.total ?? 0,
    productsSoldCount: revenueData?.vendorOpportunitiesRevenue.productsSoldCount ?? 0,
    totalAftermarketProfit: revenueData?.vendorOpportunitiesRevenue.totalAftermarketProfit ?? 0,
    totalFinanceInsuranceProfit: revenueData?.vendorOpportunitiesRevenue.totalFinanceInsuranceProfit ?? 0,
  };
}
